import { WordPress } from '../../../../wordpress/wordpress_api'
import { CMSState } from '../../../../cms/blocks/types.gen'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { IOWidget, Action, mk_widget, AsyncState, any, async, Option, onlyIf, wait, loadingAsyncState, HttpResult, some, none } from 'widgets-for-react'
import { PublicSiteState } from '../../../../public_site.main.mod'
import React from 'react'
import { decodeHTMLEntities } from '../../../../../helpers'
import { getImageAttribute } from '../../../utils/extra_images.mod'
import { link, linkWrapper } from '../../../../cms/blocks/utils.gen'
import { Map } from 'immutable'
import { SimpelPage } from '../../../../../utils'


export type GridHighlightsBlockProps<extra_images> = {
  block_index: number
  title: string
  intro: string
  conferences: number[]
  pages: number[]
  webinars: number[]
  data: Map<number, AsyncState<SimpelPage<WordPress.PostTypeResponse>>>
  total_pages: Option<number>
  size: number
  current_page: number
}

export type GridHighlightsBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  content_classname: string
  intro_classname: string
  visual_classname: string
}>

export function GridHighlightsBlock<customBlockData, extra_images>(
  props: GridHighlightsBlockProps<extra_images>,
  options?: { style: GridHighlightsBlockStyle; key?: string }
): (
  s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>
) => IOWidget<PublicSiteState, Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>> {
  return (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => (public_state: PublicSiteState) => {

    const setBlockState = <K extends keyof GridHighlightsBlockProps<extra_images>>(prop: K, newValue: GridHighlightsBlockProps<extra_images>[K]) => (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>): CMSState<customBlockData, extra_images, PublicSite_CustomPageData> => {
      if (!s.blocks.has(props.block_index)) return s
      let current_block = s.blocks.get(props.block_index) as any
      if (current_block.kind != 'gridHighlightsBlock') return s
      current_block = ({
        ...current_block,
        value: {
          ...current_block.value,
          fst: {
            ...current_block.value.fst,
            [prop]: newValue
          },

        }
      })
      return ({ ...s, blocks: s.blocks.set(props.block_index, current_block) })
    }

    return any<Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>>()([

      onlyIf(props.total_pages.v.kind == 'l' || (props.data.count() < props.total_pages.v.v),
        wait<Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>>(500)(() => s1 => {
          let has_next = props.total_pages.v.kind == 'l' || (props.data.count() < props.total_pages.v.v)
          if (!has_next) return s1

          return setBlockState('data', props.data.set(props.current_page, loadingAsyncState(() =>
            WordPress.api<WordPress.PostTypeResponse>()
              .select('post')
              .where({
                post_type: "page,conference,webinar",
                per_page: props.size, 
                page: props.current_page,
                include: (props.pages || [])
                  .concat(props.conferences || [])
                  .concat(props.webinars || [])
                  .reduce((xs, x) => xs + x + ",", "")
              })
              .getMultiple()
              .then(response => {
                if (response.status != 200) return ({ kind: 'failed', status: response.status }) as HttpResult<SimpelPage<WordPress.PostTypeResponse>>
                return ({
                  kind: 'result', status: response.status, value: {
                    items: response.data,
                    total_pages: Number(response.headers["x-wp-totalpages"]),
                    total_items: Number(response.headers["x-wp-total"]),
                    page_size: props.size,
                    current_page: props.current_page
                  }
                }) as HttpResult<SimpelPage<WordPress.PostTypeResponse>>
              })
          )))(s1)
        })
      ),

      any<Action<Map<number, AsyncState<SimpelPage<WordPress.PostTypeResponse>>>>>()(
        props.data.keySeq().map(k =>
          async<SimpelPage<WordPress.PostTypeResponse>>()(props.data.get(k))
            .map(a => (s) => s.set(k, a(s.get(k))))
        ).toArray()
      ).map(a => s1 => {
        let newData = a(props.data)
        let first_entry = newData.get(1)
        s1 = setBlockState('data', newData)(s1)
        s1 = setBlockState('total_pages', first_entry != null && first_entry.kind == 'loaded' ? some(first_entry.value.total_pages) : none())(s1)
        s1 = setBlockState('current_page', first_entry != null && first_entry.kind == 'loaded' ? props.current_page + 1 : props.current_page)(s1)

        return s1
      }),


      mk_widget({
        run: (_) => (
          <section className="block block--highlights highlights hgs">
            <div className="hgs__inner container container--m gutter--m spacing--m">
              {(props.title || props.intro) && <header className="hgs__header">
                {props.title && <h2 className="hgs__title typo--l ff--primary">{decodeHTMLEntities(props.title)}</h2>}
                {props.intro && <p>{decodeHTMLEntities(props.intro)}</p>}
              </header>}
              <div className="hgs__content">
                <ul className="hgs__grid">
                  {props.data.toIndexedSeq()
                    .toArray()
                    .reduce((xs, x) => x.kind == 'loaded' ? xs.concat(x.value.items) : xs, Array<WordPress.PostTypeResponse>())
                    .map((value) => (
                      <li onClick={() => {
                        if (value.type == "publication") {
                          download(value.acf.file, new URL(value.acf.file).pathname)
                        }
                      }} key={value.id} className="hgs__item highlight hg">
                        <article className="hg__article">
                          <div className="hg__content">
                            <h3 className="hg__title typo--m ff--primary">{decodeHTMLEntities(value.title.rendered)}</h3>
                            {value.type != "publication" && link(linkWrapper({ title: '', url: value.link }), { classname: "hg__link" }, null)}
                            {/* <NavLink className="hg__link" to={value.link}></NavLink> */}
                          </div>
                          {value.featured_media_object && (
                            <div className="hg__visual fit-object">
                              <img src={getImageAttribute(value.featured_media_object, 'highlightSecond', 'source_url')}
                                width={690}
                                height={655}
                                alt={value.featured_media_object.alt_text ? value.featured_media_object.alt_text : ''}
                              />
                            </div>
                          )}
                        </article>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </section>
        ),
      }),
    ])
  }
}


function download(dataurl, filename) {
  const link = document.createElement("a");
  link.href = dataurl;
  link.download = filename;
  link.click();
}
